import { Dispatch, SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Level, Place, Price } from "../../types/entities/session"
import { Minus, Plus } from "../../components/svgCollection"
import { appStore } from "../../store/appStore"
import { SpiderManSeatIcon } from "./SpiderManSeatIcon"

const ZOOM_STEP = 0.2
const PAN_THRESHOLD = 4
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

function formatSeatPrice(price: number) {
    return price.toLocaleString("ru-RU", { currency: "RUB", style: "currency", maximumFractionDigits: 0 })
}

function getSeatHint(place: Place, isSelected: boolean, prices?: Price[]) {
    const title = `Ряд ${place.row}, место ${place.place}`

    if (place.my) {
        return { title, status: "Ваше место" }
    }
    if (!place.status) {
        return { title, status: "Занято" }
    }
    if (isSelected) {
        return { title, status: "Выбрано", price: formatSeatPrice(place.price) }
    }

    const category = prices?.find(item => item.color === place.color)?.name
    return {
        title,
        status: category ?? "Свободно",
        price: formatSeatPrice(place.price),
    }
}

type HallProps = {
    level: Level
    selectedSeats: Place[]
    setSelectedSeats?: Dispatch<SetStateAction<Place[]>>
    readOnly?: boolean
    containerHeight?: number
    fillHeight?: boolean
    initialZoomSteps?: number
    prices?: Price[]
    /** Тема Человека-паука: выбранные и занятые места — маска */
    spiderMan?: boolean
}

export function Hall({
    level,
    selectedSeats,
    setSelectedSeats,
    readOnly = false,
    containerHeight = 400,
    fillHeight = false,
    initialZoomSteps = 1,
    prices,
    spiderMan = false,
}: HallProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const isDragging = useRef(false)
    const panPointerId = useRef<number | null>(null)
    const panStart = useRef<{ x: number; y: number } | null>(null)
    const lastPos = useRef({ x: 0, y: 0 })
    // Активные касания и состояние pinch-жеста (масштабирование двумя пальцами)
    const pointersRef = useRef(new Map<number, { x: number; y: number }>())
    const pinchRef = useRef<{ lastDist: number; lastMid: { x: number; y: number } } | null>(null)
    // Был ли drag/pinch в текущем взаимодействии — чтобы не срабатывал клик по месту
    const didDragRef = useRef(false)

    const [isDraggingState, setIsDraggingState] = useState(false)
    const [zoom, setZoom] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [seatHint, setSeatHint] = useState<{ place: Place; x: number; y: number; touch: boolean } | null>(null)
    const [layoutTick, setLayoutTick] = useState(0)

    // Синхронные значения zoom/pan для обработчиков жестов (pointermove чаще, чем рендер)
    const zoomLive = useRef(zoom)
    const panLive = useRef(pan)
    useEffect(() => {
        zoomLive.current = zoom
    }, [zoom])
    useEffect(() => {
        panLive.current = pan
    }, [pan])

    const { width, height, places, objects } = level

    const getContainerHeight = () => {
        if (!containerRef.current) return fillHeight ? 400 : containerHeight
        return containerRef.current.clientHeight || (fillHeight ? 400 : containerHeight)
    }

    const getMinZoom = () => {
        if (!containerRef.current) return 1

        const containerWidth = containerRef.current.clientWidth
        const containerH = getContainerHeight()

        return Math.min(containerH / height, containerWidth / width)
    }

    const getInitialZoom = (minZoom: number) => {
        if (!fillHeight) return minZoom
        return Math.min(minZoom + initialZoomSteps * ZOOM_STEP, minZoom + 0.5)
    }

    // Внимание: зависим от размеров зала, а не от объекта level — он пересоздаётся
    // при каждом фоновом обновлении сеанса (раз в 5 секунд), и зависимость по ссылке
    // сбрасывала бы зум/позицию пользователя
    const minZoom = useMemo(() => {
        if (!containerRef.current) return 1
        const containerH = getContainerHeight()
        return Math.min(containerRef.current.clientWidth / width, containerH / height)
    }, [width, height, containerHeight, fillHeight, layoutTick])

    useEffect(() => {
        if (!fillHeight || !containerRef.current) return
        const el = containerRef.current
        const ro = new ResizeObserver(() => setLayoutTick(t => t + 1))
        ro.observe(el)
        return () => ro.disconnect()
    }, [fillHeight])

    useLayoutEffect(() => {
        setZoom(getInitialZoom(minZoom))
        setPan({ x: 0, y: 0 })
    }, [minZoom, initialZoomSteps])

    useLayoutEffect(() => {
        if (!containerRef.current) return
        setZoom(getInitialZoom(getMinZoom()))
    }, [width, height, containerHeight, fillHeight, initialZoomSteps])

    const handleSeatPress = (place: Place) => {
        if (readOnly || !setSelectedSeats) return

        setSelectedSeats(prevSeats => {
            if (prevSeats.some(seat => seat.id === place.id)) {
                return prevSeats.filter(seat => seat.id !== place.id)
            } else if (prevSeats.length < 8) {
                return [...prevSeats, place]
            } else {
                appStore.getState().setError("Достигнут максимум количества билетов за одну покупку")
                return prevSeats
            }
        })
    }

    const getPanBounds = (zoomValue: number) => {
        if (!containerRef.current) return { maxX: 0, maxY: 0 }

        const containerWidth = containerRef.current.clientWidth
        const containerHeight = containerRef.current.clientHeight

        const scaledWidth = width * zoomValue
        const scaledHeight = height * zoomValue

        const panSlack = fillHeight ? 0 : 40

        return {
            maxX: Math.max(0, (scaledWidth - containerWidth) / 2) + panSlack,
            maxY: Math.max(0, (scaledHeight - containerHeight) / 2) + panSlack,
        }
    }

    useEffect(() => {
        if (isDraggingState) return

        const { maxX, maxY } = getPanBounds(zoom)

        const minZoom = getMinZoom()
        const isMinZoom = zoom <= minZoom + 0.0001

        if (isMinZoom && !isDraggingState) {
            setTimeout(() => {
                setPan({ x: 0, y: 0 })
            }, 500)
        } else {
            setTimeout(() => {
                setPan(p => ({
                    x: clamp(p.x, -maxX, maxX),
                    y: clamp(p.y, -maxY, maxY)
                }))
            }, 500)
        }
    }, [zoom, pan.x, pan.y, isDraggingState])

    const zoomIn = () => {
        const minZoom = getMinZoom()
        setIsDraggingState(false)
        setZoom(z => Math.min(z + ZOOM_STEP, minZoom + 0.5))
    }

    const zoomOut = () => {
        const minZoom = getMinZoom()
        setZoom(z => Math.max(z - ZOOM_STEP, minZoom))
    }

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

    const applyZoomPan = (nextZoom: number, nextPan: { x: number; y: number }) => {
        const { maxX, maxY } = getPanBounds(nextZoom)
        const clamped = { x: clamp(nextPan.x, -maxX, maxX), y: clamp(nextPan.y, -maxY, maxY) }
        zoomLive.current = nextZoom
        panLive.current = clamped
        setZoom(nextZoom)
        setPan(clamped)
    }

    const onPointerDown = (e: React.PointerEvent) => {
        if (e.pointerType === "mouse" && e.button !== 0) return

        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

        if (pointersRef.current.size === 1) {
            didDragRef.current = false
            panPointerId.current = e.pointerId
            panStart.current = { x: e.clientX, y: e.clientY }
            lastPos.current = { x: e.clientX, y: e.clientY }
        } else if (pointersRef.current.size === 2) {
            // Второй палец — начинаем pinch-масштабирование
            const [p1, p2] = [...pointersRef.current.values()]
            pinchRef.current = {
                lastDist: Math.hypot(p2.x - p1.x, p2.y - p1.y),
                lastMid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
            }
            panPointerId.current = null
            panStart.current = null
            isDragging.current = true
            didDragRef.current = true
            setIsDraggingState(true)
            setSeatHint(null)
            for (const id of pointersRef.current.keys()) {
                try {
                    e.currentTarget.setPointerCapture(id)
                } catch {
                    /* палец мог уже подняться */
                }
            }
        }
    }

    const onPointerMove = (e: React.PointerEvent) => {
        if (!pointersRef.current.has(e.pointerId) || !containerRef.current) return

        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

        // Pinch: масштабируем вокруг средней точки между пальцами
        if (pinchRef.current && pointersRef.current.size >= 2) {
            const [p1, p2] = [...pointersRef.current.values()]
            const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y)
            if (dist <= 0 || pinchRef.current.lastDist <= 0) return

            const rect = containerRef.current.getBoundingClientRect()
            const cx = rect.left + rect.width / 2
            const cy = rect.top + rect.height / 2
            const mid = { x: (p1.x + p2.x) / 2 - cx, y: (p1.y + p2.y) / 2 - cy }
            const lastMid = {
                x: pinchRef.current.lastMid.x - cx,
                y: pinchRef.current.lastMid.y - cy,
            }

            const minZ = getMinZoom()
            const prevZoom = zoomLive.current
            const nextZoom = clamp(prevZoom * (dist / pinchRef.current.lastDist), minZ, minZ + 0.5)
            const ratio = nextZoom / prevZoom

            // Точка зала под серединой пальцев остаётся на месте
            applyZoomPan(nextZoom, {
                x: mid.x - (lastMid.x - panLive.current.x) * ratio,
                y: mid.y - (lastMid.y - panLive.current.y) * ratio,
            })

            pinchRef.current = {
                lastDist: dist,
                lastMid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
            }
            return
        }

        if (panPointerId.current !== e.pointerId) return

        if (!isDragging.current && panStart.current) {
            const dx = e.clientX - panStart.current.x
            const dy = e.clientY - panStart.current.y
            if (Math.hypot(dx, dy) < PAN_THRESHOLD) return

            isDragging.current = true
            didDragRef.current = true
            setIsDraggingState(true)
            e.currentTarget.setPointerCapture(e.pointerId)
        }

        if (!isDragging.current) return

        const dx = e.clientX - lastPos.current.x
        const dy = e.clientY - lastPos.current.y
        lastPos.current = { x: e.clientX, y: e.clientY }

        applyZoomPan(zoomLive.current, {
            x: panLive.current.x + dx,
            y: panLive.current.y + dy,
        })
    }

    const onPointerUp = (e: React.PointerEvent) => {
        if (!pointersRef.current.has(e.pointerId)) return

        pointersRef.current.delete(e.pointerId)
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId)
        }

        if (pinchRef.current && pointersRef.current.size < 2) {
            pinchRef.current = null
            const rest = [...pointersRef.current.entries()][0]
            if (rest) {
                // Один палец остался — продолжаем панорамирование им
                panPointerId.current = rest[0]
                lastPos.current = { ...rest[1] }
                panStart.current = { ...rest[1] }
                isDragging.current = true
                return
            }
        }

        if (pointersRef.current.size === 0) {
            panPointerId.current = null
            panStart.current = null
            isDragging.current = false
            setIsDraggingState(false)
        } else if (panPointerId.current === e.pointerId) {
            const rest = [...pointersRef.current.entries()][0]
            panPointerId.current = rest[0]
            lastPos.current = { ...rest[1] }
            panStart.current = { ...rest[1] }
        }
    }

    useEffect(() => {
        if (isDraggingState) setSeatHint(null)
    }, [isDraggingState])

    const hint = seatHint
        ? getSeatHint(
              seatHint.place,
              selectedSeats.some(seat => seat.id === seatHint.place.id),
              prices,
          )
        : null

    const showSeatHint = (place: Place, e: React.PointerEvent<HTMLButtonElement>) => {
        if (isDragging.current) return
        setSeatHint({ place, x: e.clientX, y: e.clientY, touch: e.pointerType !== "mouse" })
    }

    return (
        <div
            data-sheet-drag-exempt
            className={`relative w-full ${fillHeight ? "h-full min-h-[140px] min-w-0 overflow-hidden" : ""}`}
            ref={containerRef}
            style={fillHeight ? undefined : { height: containerHeight }}>
            {hint &&
                seatHint &&
                createPortal(
                    <div
                        className={`pointer-events-none fixed z-[300] -translate-x-1/2 overflow-hidden rounded-[12px] border border-white/12 shadow-[0_8px_24px_rgba(0,0,0,0.45)] ${
                            seatHint.touch
                                ? "-translate-y-[calc(100%+48px)] px-4 py-2.5"
                                : "-translate-y-[calc(100%+8px)] px-2.5 py-1.5"
                        }`}
                        style={{
                            left: seatHint.x,
                            top: seatHint.y,
                            background: "linear-gradient(160deg, #1e2f58 0%, #1a1438 100%)",
                        }}>
                        <div
                            className={`leading-tight text-white ${seatHint.touch ? "text-[16px]" : "text-[11px]"}`}
                            style={{ fontFamily: BODY, fontWeight: 700 }}>
                            {hint.title}
                        </div>
                        <div
                            className={`mt-0.5 text-white/55 ${seatHint.touch ? "text-[14px]" : "text-[10px]"}`}
                            style={{ fontFamily: MONO, fontWeight: 600 }}>
                            {hint.status}
                        </div>
                        {hint.price && hint.status !== hint.price && (
                            <div
                                className={`mt-0.5 text-[#349be6] ${seatHint.touch ? "text-[14px]" : "text-[10px]"}`}
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                {hint.price}
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
            <div
                className={`absolute inset-0 ${fillHeight ? "overflow-clip" : "overflow-hidden"}`}
                data-sheet-drag-exempt>
                <div
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onPointerLeave={onPointerUp}
                    className="absolute cursor-grab select-none active:cursor-grabbing"
                    style={{
                        touchAction: "none",
                        width,
                        height,
                        left: "50%",
                        top: "50%",
                        transition: isDraggingState ? "none" : "transform 300ms ease-out",
                        transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: "center center"
                    }}>
                    {places.map((place, index) => {
                        const isSelected = selectedSeats.some(seat => seat.id === place.id)
                        const isUnavailable = place.my || !place.status
                        const isSpiderSelected = spiderMan && (isSelected || place.my)
                        const isSpiderOccupied = spiderMan && !place.status && !place.my
                        const isSpiderSeat = isSpiderSelected || isSpiderOccupied
                        return (
                            <button
                                type="button"
                                onClick={() => {
                                    if (readOnly || isUnavailable || didDragRef.current) return
                                    handleSeatPress(place)
                                }}
                                onPointerEnter={e => showSeatHint(place, e)}
                                onPointerMove={e => showSeatHint(place, e)}
                                onPointerLeave={() => setSeatHint(null)}
                                key={index}
                                className={`absolute flex items-center justify-center font-extrabold text-white ${
                                    isSpiderSeat ? "rounded-[6px] p-0" : isSelected ? "rounded-full" : "rounded-[5px]"
                                } ${
                                    fillHeight ? "text-[19px]/[19px]" : "text-[13px]/[13px]"
                                } ${readOnly ? "cursor-default" : isUnavailable ? "cursor-not-allowed" : "cursor-pointer"}`}
                                disabled={readOnly}
                                style={{
                                    left: place.x,
                                    top: place.y,
                                    width: place.width,
                                    height: place.height,
                                    backgroundColor: isSpiderSeat
                                        ? "transparent"
                                        : isSelected || place.my
                                          ? "#E4206B"
                                          : place.color,
                                    opacity: isSpiderSeat ? 1 : isUnavailable ? 0.6 : 1,
                                    // Свободные места рисуем чуть меньше своего бокса — больше воздуха между рядами
                                    transform: isSelected || isSpiderSelected ? "scale(1.45)" : "scale(0.78)",
                                    zIndex: isSelected || isSpiderSelected ? 2 : undefined,
                                    boxShadow: isSpiderSelected
                                        ? "0 0 14px rgba(226,35,40,0.7)"
                                        : isSelected
                                          ? "0 0 16px rgba(228,32,107,0.65)"
                                          : undefined,
                                    transition: "transform 150ms ease-out, box-shadow 150ms ease-out, border-radius 150ms ease-out",
                                }}>
                                {isSpiderSeat ? (
                                    <SpiderManSeatIcon
                                        variant={isSpiderSelected ? "selected" : "occupied"}
                                        className="h-full w-full"
                                    />
                                ) : isSelected ? (
                                    place.place
                                ) : null}
                            </button>
                        )
                    })}
                    {objects.map((object, index) => {
                        // Номера рядов — крупно; текстовые метки («Вход в зал») — компактнее,
                        // с переносом, чтобы не вылезали за границы зала
                        const isNumericLabel = object.slug === "screen" || /^\d+$/.test(object.name?.trim() ?? "")
                        return (
                        <div
                            key={index}
                            className={`absolute flex items-center justify-center rounded-[5px] tracking-[-0.078px] text-[rgba(255,255,255,0.75)] ${
                                isNumericLabel
                                    ? "whitespace-nowrap text-[19px]/[22px] font-semibold"
                                    : "text-center text-[15px]/[18px] font-semibold break-words"
                            }`}
                            style={{
                                left: object.x,
                                top: object.y,
                                width: object.width,
                                height: object.height
                            }}>
                            {object.slug === "screen" ? (
                                <div className="flex w-full flex-col items-center">
                                    <svg
                                        width="100%"
                                        height="20"
                                        viewBox="0 0 100 20"
                                        preserveAspectRatio="none"
                                        aria-hidden
                                        style={{
                                            filter: "drop-shadow(0 4px 10px rgba(244,246,255,0.55)) drop-shadow(0 14px 36px rgba(244,246,255,0.35))",
                                        }}>
                                        <path
                                            d="M2 16 Q 50 3 98 16"
                                            stroke="#f4f6ff"
                                            strokeWidth="7"
                                            fill="none"
                                            strokeLinecap="round"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    </svg>
                                    <span className="mt-1.5">Экран</span>
                                </div>
                            ) : (
                                object.name
                            )}
                        </div>
                        )
                    })}
                </div>
            </div>

            <div
                className={
                    fillHeight
                        ? "pointer-events-none absolute top-1/2 z-20 flex -translate-y-1/2 flex-col gap-2 right-1.5"
                        : "absolute top-[50%] -right-[22px] flex -translate-y-1/2 flex-col gap-2"
                }
                data-sheet-drag-exempt>
                <button
                    disabled={zoom >= getMinZoom() + 0.5}
                    onClick={zoomIn}
                    className={`bg-background ${fillHeight ? "pointer-events-auto " : ""}${
                        zoom >= getMinZoom() + 0.5 ? "opacity-50" : ""
                    } shadow-bg flex h-[34px] w-[38px] cursor-pointer items-center justify-center rounded-[7px] shadow-[0_0_20px_rgba(24,19,59,0.60)]`}>
                    <Plus />
                </button>

                <button
                    disabled={zoom <= getMinZoom()}
                    onClick={zoomOut}
                    className={`bg-background ${fillHeight ? "pointer-events-auto " : ""}${
                        zoom <= getMinZoom() ? "opacity-50" : ""
                    } shadow-bg flex h-[34px] w-[38px] cursor-pointer items-center justify-center rounded-[7px] shadow-[0_0_20px_rgba(24,19,59,0.60)]`}>
                    <Minus />
                </button>
            </div>
        </div>
    )
}
